import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    ModalFooter, VStack, HStack, Flex, Box, Text, Badge, Icon, IconButton,
    Button, Select, Textarea, Spinner, Tabs, TabList, TabPanels, Tab, TabPanel,
    useToast, FormControl, FormLabel,
} from "@chakra-ui/react";
import {
    MdDownload, MdUpload, MdPictureAsPdf, MdSend, MdAttachFile,
    MdHistory, MdComment, MdPerson, MdAssignment,
} from "react-icons/md";
import axios from "axios";
import { BASE_URL } from "config/api";
import { usePermissions } from "hooks/usePermissions";

// ── Badges ──────────────────────────────────────────────────────────────────
const STATUT_CONFIG = {
    Ouverte   : { bg: "#EBF4FF", border: "#BEE3F8", text: "#1A56DB", label: "Ouverte"  },
    "En cours": { bg: "#FFF9E7", border: "#FFE899", text: "#8D6E00", label: "En cours" },
    Resolue   : { bg: "#EDF7F1", border: "#CDE8D9", text: "#006133", label: "Résolue"  },
    Cloturee  : { bg: "#F7F7F7", border: "#E2E8F0", text: "#6B7280", label: "Clôturée" },
};
const GRAVITE_MAP = { Critique: "red", Moyen: "orange", Faible: "green" };

const StatutBadge = ({ statut }) => {
    const cfg = STATUT_CONFIG[statut] || STATUT_CONFIG["Ouverte"];
    return (
        <HStack spacing={2} bg={cfg.bg} border="1px solid" borderColor={cfg.border}
            px={3} py={1} borderRadius="full">
            <Box w="7px" h="7px" borderRadius="full" bg={cfg.text} />
            <Text fontSize="12px" fontWeight="600" color={cfg.text}>{cfg.label}</Text>
        </HStack>
    );
};

const fmtDate = (dt) => dt ? new Date(dt).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—";

const fmtSize = (bytes) => {
    if (!bytes) return "0 o";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1048576).toFixed(1)} Mo`;
};

// ── Composant principal ──────────────────────────────────────────────────────
export default function ReclamationDetailModal({ isOpen, onClose, reclamationId, onUpdated }) {
    const { hasPermission } = usePermissions();
    const toast = useToast();
    const fileInputRef = useRef();

    const [rec, setRec]         = useState(null);
    const [loading, setLoading] = useState(false);
    const [users, setUsers]     = useState([]);

    // Statut
    const [actionLoading, setActionLoading] = useState(false);
    const [showMotif, setShowMotif]         = useState(false);
    const [motifCloture, setMotifCloture]   = useState("");

    // Assignation
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignUserId, setAssignUserId]   = useState("");

    // Commentaire
    const [comment, setComment]               = useState("");
    const [commentLoading, setCommentLoading] = useState(false);

    // Pièces jointes
    const [uploadLoading, setUploadLoading] = useState(false);

    // ── Chargement ────────────────────────────────────────────────────────────
    const fetchRec = useCallback(async () => {
        if (!reclamationId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/reclamations/${reclamationId}`, { withCredentials: true });
            setRec(res.data);
            setAssignUserId(res.data.assigne_a?.id ? String(res.data.assigne_a.id) : "");
        } catch {
            toast({ title: "Impossible de charger la réclamation", status: "error", isClosable: true });
        } finally {
            setLoading(false);
        }
    }, [reclamationId, toast]);

    useEffect(() => {
        if (!isOpen || !reclamationId) return;
        setShowMotif(false);
        setMotifCloture("");
        setComment("");
        fetchRec();
    }, [isOpen, reclamationId, fetchRec]);

    useEffect(() => {
        if (!isOpen) return;
        axios.get(`${BASE_URL}/users`, { withCredentials: true })
            .then((r) => setUsers(r.data || []))
            .catch(() => {});
    }, [isOpen]);

    // ── Actions statut ────────────────────────────────────────────────────────
    const handleStatut = async (newStatut) => {
        if (newStatut === "Cloturee" && !showMotif) { setShowMotif(true); return; }
        setActionLoading(true);
        try {
            await axios.put(
                `${BASE_URL}/reclamations/${reclamationId}/statut`,
                { statut: newStatut, ...(newStatut === "Cloturee" ? { motif_cloture: motifCloture } : {}) },
                { withCredentials: true }
            );
            toast({ title: "Statut mis à jour", status: "success", isClosable: true });
            setShowMotif(false);
            setMotifCloture("");
            await fetchRec();
            if (onUpdated) onUpdated();
        } catch (e) {
            toast({ title: e.response?.data?.message || "Erreur mise à jour statut", status: "error", isClosable: true });
        } finally {
            setActionLoading(false);
        }
    };

    // ── Assignation ──────────────────────────────────────────────────────────
    const handleAssign = async () => {
        setAssignLoading(true);
        try {
            await axios.put(
                `${BASE_URL}/reclamations/${reclamationId}/assigner`,
                { user_id: assignUserId || null },
                { withCredentials: true }
            );
            toast({ title: "Assignation mise à jour", status: "success", isClosable: true });
            await fetchRec();
            if (onUpdated) onUpdated();
        } catch (e) {
            toast({ title: e.response?.data?.message || "Erreur d'assignation", status: "error", isClosable: true });
        } finally {
            setAssignLoading(false);
        }
    };

    // ── Commentaire ──────────────────────────────────────────────────────────
    const handleAddComment = async () => {
        if (!comment.trim()) return;
        setCommentLoading(true);
        try {
            await axios.post(
                `${BASE_URL}/reclamations/${reclamationId}/commentaires`,
                { commentaire: comment.trim() },
                { withCredentials: true }
            );
            setComment("");
            await fetchRec();
        } catch (e) {
            toast({ title: e.response?.data?.message || "Erreur ajout commentaire", status: "error", isClosable: true });
        } finally {
            setCommentLoading(false);
        }
    };

    // ── Upload pièce jointe ───────────────────────────────────────────────────
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast({ title: "Fichier trop volumineux (max 5 Mo)", status: "warning", isClosable: true });
            return;
        }
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const b64 = ev.target.result.split(",")[1];
            setUploadLoading(true);
            try {
                await axios.post(
                    `${BASE_URL}/reclamations/${reclamationId}/pieces-jointes`,
                    { nom_fichier: file.name, type_mime: file.type || "application/octet-stream", taille: file.size, donnees_base64: b64 },
                    { withCredentials: true }
                );
                toast({ title: "Fichier ajouté", status: "success", isClosable: true });
                await fetchRec();
            } catch (err) {
                toast({ title: err.response?.data?.message || "Erreur upload", status: "error", isClosable: true });
            } finally {
                setUploadLoading(false);
                e.target.value = "";
            }
        };
        reader.readAsDataURL(file);
    };

    // ── Téléchargement ────────────────────────────────────────────────────────
    const handleDownload = (pieceId, nomFichier) => {
        const url = `${BASE_URL}/reclamations/${reclamationId}/pieces-jointes/${pieceId}/download`;
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", nomFichier || "fichier");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // ── Export PDF ────────────────────────────────────────────────────────────
    const handleExportPDF = async () => {
        try {
            const res = await axios.get(
                `${BASE_URL}/reclamations/${reclamationId}/pdf`,
                { responseType: "blob", withCredentials: true }
            );
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.setAttribute("download", `reclamation_${rec?.numero_reclamation || reclamationId}.pdf`);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch {
            toast({ title: "Erreur export PDF", status: "error", isClosable: true });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    const canEdit    = hasPermission("reclamations_edit");
    const isArchived = !!(rec?.archivee_at);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
            <ModalContent
                boxShadow="2xl"
                maxH="100vh"
                h="100vh"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                position="fixed"
                right={0}
                top={0}
                bottom={0}
                m={0}
                maxW="980px"
                w="980px"
                borderRadius="0"
                borderTopLeftRadius="12px"
                borderBottomLeftRadius="12px"
            >
                <ModalHeader borderBottom="1px solid" borderColor="gray.200" borderTopLeftRadius="12px">
                    {loading ? <Spinner size="sm" color="red.500" /> : rec ? (
                        <VStack align="start" spacing={2}>
                            <HStack spacing={3} flexWrap="wrap">
                                <Text fontWeight="bold" color="red.600" fontSize="lg">{rec.numero_reclamation}</Text>
                                <StatutBadge statut={rec.statut} />
                                {rec.niveau_gravite && (
                                    <Badge colorScheme={GRAVITE_MAP[rec.niveau_gravite] || "gray"} variant="subtle" borderRadius="full" px={2}>
                                        {rec.niveau_gravite}
                                    </Badge>
                                )}
                                {isArchived && (
                                    <Badge colorScheme="gray" variant="solid" borderRadius="full" px={2}>
                                        Archivée le {rec.archivee_at ? new Date(rec.archivee_at).toLocaleDateString("fr-FR") : ""}
                                    </Badge>
                                )}
                            </HStack>
                            <Text fontSize="sm" color="gray.500">{rec.type_reclamation} · {rec.service_concerne}</Text>
                        </VStack>
                    ) : "Détail réclamation"}
                </ModalHeader>
                <ModalCloseButton top={4} right={4} borderRadius="full" _hover={{ bg: 'gray.100' }} />

                <ModalBody py={4} flex={1} overflowY="auto">
                    {loading ? (
                        <Flex justify="center" py={10}><Spinner size="xl" color="red.500" /></Flex>
                    ) : !rec ? (
                        <Text color="gray.500" textAlign="center">Réclamation introuvable</Text>
                    ) : (
                        <Tabs variant="enclosed" colorScheme="red">
                            <TabList>
                                <Tab><Icon as={MdAssignment} mr={1} />Infos</Tab>
                                <Tab><Icon as={MdHistory} mr={1} />Historique ({rec.historique?.length || 0})</Tab>
                                <Tab><Icon as={MdComment} mr={1} />Commentaires ({rec.commentaires?.length || 0})</Tab>
                                <Tab><Icon as={MdAttachFile} mr={1} />Pièces jointes ({rec.pieces_jointes?.length || 0})</Tab>
                            </TabList>

                            <TabPanels>
                                {/* ── Infos ── */}
                                <TabPanel>
                                    <VStack align="stretch" spacing={5}>

                                        {/* Actions lifecycle (masquées si archivée) */}
                                        {canEdit && !isArchived && (
                                            <Box bg="gray.50" borderRadius="8px" p={4}>
                                                <Text fontWeight="semibold" mb={3} color="gray.700">Actions</Text>
                                                <HStack spacing={3} flexWrap="wrap">
                                                    {rec.statut === "Ouverte" && (
                                                        <Button colorScheme="orange" size="sm" isLoading={actionLoading}
                                                            onClick={() => handleStatut("En cours")}>
                                                            Passer En cours
                                                        </Button>
                                                    )}
                                                    {rec.statut === "En cours" && (
                                                        <Button colorScheme="green" size="sm" isLoading={actionLoading}
                                                            onClick={() => handleStatut("Resolue")}>
                                                            Marquer Résolue
                                                        </Button>
                                                    )}
                                                    {rec.statut === "Resolue" && !showMotif && (
                                                        <Button colorScheme="gray" size="sm" onClick={() => setShowMotif(true)}>
                                                            Clôturer
                                                        </Button>
                                                    )}
                                                </HStack>
                                                {showMotif && rec.statut === "Resolue" && (
                                                    <VStack align="stretch" spacing={2} mt={3}>
                                                        <FormControl isRequired>
                                                            <FormLabel fontSize="sm">Motif de clôture</FormLabel>
                                                            <Textarea value={motifCloture} onChange={(e) => setMotifCloture(e.target.value)}
                                                                placeholder="Décrivez la résolution..." size="sm" rows={3} />
                                                        </FormControl>
                                                        <HStack>
                                                            <Button colorScheme="red" size="sm" isLoading={actionLoading}
                                                                isDisabled={!motifCloture.trim()}
                                                                onClick={() => handleStatut("Cloturee")}>
                                                                Confirmer la clôture
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => { setShowMotif(false); setMotifCloture(""); }}>
                                                                Annuler
                                                            </Button>
                                                        </HStack>
                                                    </VStack>
                                                )}
                                            </Box>
                                        )}

                                        {/* Informations générales */}
                                        <Box>
                                            <Text fontWeight="semibold" color="gray.700" mb={3}>Informations</Text>
                                            <Box border="1px solid" borderColor="gray.200" borderRadius="8px" overflow="hidden">
                                                {[
                                                    ["Type",              rec.type_reclamation],
                                                    ["Gravité",           rec.niveau_gravite],
                                                    ["Service concerné",  rec.service_concerne],
                                                    ["Référence dossier", rec.reference_dossier || "—"],
                                                    ["Date de création",  fmtDate(rec.created_at)],
                                                    ["Créateur",          rec.createur ? `${rec.createur.prenom || ""} ${rec.createur.nom || ""}`.trim() : "—"],
                                                    ["Assigné à",         rec.assigne_a ? `${rec.assigne_a.prenom || ""} ${rec.assigne_a.nom || ""}`.trim() : "Non assigné"],
                                                    ["Date résolution",   rec.date_resolution ? fmtDate(rec.date_resolution) : "—"],
                                                    ["Date clôture",      rec.date_cloture ? fmtDate(rec.date_cloture) : "—"],
                                                    ["Motif clôture",     rec.motif_cloture || "—"],
                                                ].map(([label, value], idx) => (
                                                    <Flex key={label} bg={idx % 2 === 0 ? "gray.50" : "white"} px={4} py={2}
                                                        borderBottom={idx < 9 ? "1px solid" : "none"} borderColor="gray.200">
                                                        <Text fontSize="sm" color="gray.500" w="180px" flexShrink={0} fontWeight="medium">{label}</Text>
                                                        <Text fontSize="sm" color="gray.800">{value}</Text>
                                                    </Flex>
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Liens mission / avis */}
                                        {(rec.mission_id || rec.avis_id) && (
                                            <Box>
                                                <Text fontWeight="semibold" color="gray.700" mb={2}>Liens</Text>
                                                <HStack spacing={3}>
                                                    {rec.mission_id && (
                                                        <Button as="a" href={`/admin/missions/${rec.mission_id}`}
                                                            variant="outline" size="sm" colorScheme="blue">
                                                            Mission #{rec.mission_id}
                                                        </Button>
                                                    )}
                                                    {rec.avis_id && (
                                                        <Button as="a" href={`/admin/avis?id=${rec.avis_id}`}
                                                            variant="outline" size="sm" colorScheme="purple">
                                                            Avis #{rec.avis_id}
                                                        </Button>
                                                    )}
                                                </HStack>
                                            </Box>
                                        )}

                                        {/* Description */}
                                        <Box>
                                            <Text fontWeight="semibold" color="gray.700" mb={2}>Description</Text>
                                            <Box bg="gray.50" borderRadius="8px" p={4} border="1px solid" borderColor="gray.200">
                                                <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{rec.description}</Text>
                                            </Box>
                                        </Box>

                                        {/* Assignation */}
                                        {canEdit && !isArchived && (
                                            <Box>
                                                <Text fontWeight="semibold" color="gray.700" mb={2}>
                                                    <Icon as={MdPerson} mr={1} />Assigner à
                                                </Text>
                                                <HStack>
                                                    <Select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)} size="sm" flex={1}>
                                                        <option value="">— Non assigné —</option>
                                                        {users.map((u) => (
                                                            <option key={u.id} value={String(u.id)}>
                                                                {u.prenom || ""} {u.nom || ""} {u.role ? `(${u.role})` : ""}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    <Button colorScheme="blue" size="sm" isLoading={assignLoading} onClick={handleAssign}>
                                                        Valider
                                                    </Button>
                                                </HStack>
                                            </Box>
                                        )}
                                    </VStack>
                                </TabPanel>

                                {/* ── Historique ── */}
                                <TabPanel>
                                    {!rec.historique?.length ? (
                                        <Text color="gray.400" textAlign="center" py={8}>Aucun historique</Text>
                                    ) : (
                                        <VStack align="stretch" spacing={0}>
                                            {rec.historique.map((h, i) => (
                                                <HStack key={h.id} align="start" spacing={4} px={2} py={3}
                                                    bg={i % 2 === 0 ? "gray.50" : "white"} borderRadius="6px">
                                                    <Box w="10px" h="10px" borderRadius="full" bg="blue.400" mt={1} flexShrink={0} />
                                                    <VStack align="start" spacing={0} flex={1}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">{h.action}</Text>
                                                        {(h.ancien_statut || h.nouveau_statut) && (
                                                            <HStack spacing={1} fontSize="xs" color="gray.500">
                                                                {h.ancien_statut && <Text>{h.ancien_statut}</Text>}
                                                                {h.ancien_statut && h.nouveau_statut && <Text>→</Text>}
                                                                {h.nouveau_statut && <Text fontWeight="medium" color="green.600">{h.nouveau_statut}</Text>}
                                                            </HStack>
                                                        )}
                                                        {h.details && <Text fontSize="xs" color="gray.500">{h.details}</Text>}
                                                        <Text fontSize="xs" color="gray.400">
                                                            {(h.auteur || h.user) ? `${(h.auteur || h.user).prenom || ""} ${(h.auteur || h.user).nom || ""}`.trim() : "Système"} · {fmtDate(h.created_at)}
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    )}
                                </TabPanel>

                                {/* ── Commentaires ── */}
                                <TabPanel>
                                    <VStack align="stretch" spacing={4}>
                                        {canEdit && !isArchived && (
                                            <HStack align="end">
                                                <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                                                    placeholder="Ajouter un commentaire..." size="sm" rows={3} flex={1} />
                                                <IconButton icon={<MdSend />} colorScheme="blue" size="sm" aria-label="Envoyer"
                                                    isLoading={commentLoading} isDisabled={!comment.trim()} onClick={handleAddComment} />
                                            </HStack>
                                        )}
                                        {!rec.commentaires?.length ? (
                                            <Text color="gray.400" textAlign="center" py={6}>Aucun commentaire</Text>
                                        ) : (
                                            rec.commentaires.map((c) => (
                                                <Box key={c.id} bg="blue.50" borderRadius="8px" p={3} border="1px solid" borderColor="blue.200">
                                                    <HStack justify="space-between" mb={1}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                                                            {(c.auteur || c.user) ? `${(c.auteur || c.user).prenom || ""} ${(c.auteur || c.user).nom || ""}`.trim() : "—"}
                                                        </Text>
                                                        <Text fontSize="xs" color="gray.400">{fmtDate(c.created_at)}</Text>
                                                    </HStack>
                                                    <Text fontSize="sm" color="gray.700">{c.commentaire}</Text>
                                                </Box>
                                            ))
                                        )}
                                    </VStack>
                                </TabPanel>

                                {/* ── Pièces jointes ── */}
                                <TabPanel>
                                    <VStack align="stretch" spacing={4}>
                                        {canEdit && !isArchived && (
                                            <HStack>
                                                <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
                                                <Button leftIcon={<Icon as={MdUpload} />} colorScheme="green" size="sm"
                                                    isLoading={uploadLoading} onClick={() => fileInputRef.current?.click()}>
                                                    Ajouter un fichier
                                                </Button>
                                                <Text fontSize="xs" color="gray.400">Max 5 Mo · Max 5 fichiers</Text>
                                            </HStack>
                                        )}
                                        {!rec.pieces_jointes?.length ? (
                                            <Text color="gray.400" textAlign="center" py={6}>Aucune pièce jointe</Text>
                                        ) : (
                                            rec.pieces_jointes.map((pj) => (
                                                <Flex key={pj.id} align="center" justify="space-between"
                                                    bg="gray.50" borderRadius="8px" px={4} py={3}
                                                    border="1px solid" borderColor="gray.200">
                                                    <HStack spacing={3}>
                                                        <Icon as={MdAttachFile} color="gray.500" />
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontSize="sm" fontWeight="medium" color="gray.800">{pj.nom_fichier}</Text>
                                                            <Text fontSize="xs" color="gray.400">{fmtSize(pj.taille)} · {pj.type_mime}</Text>
                                                        </VStack>
                                                    </HStack>
                                                    <IconButton icon={<MdDownload />} size="sm" variant="ghost" colorScheme="blue"
                                                        aria-label="Télécharger" onClick={() => handleDownload(pj.id, pj.nom_fichier)} />
                                                </Flex>
                                            ))
                                        )}
                                    </VStack>
                                </TabPanel>
                            </TabPanels>
                        </Tabs>
                    )}
                </ModalBody>

                <ModalFooter py={4} px={6} borderTop="1px solid" borderColor="gray.200" bg="gray.50">
                    <HStack spacing={3}>
                        {rec && !loading && (
                            <Button leftIcon={<Icon as={MdPictureAsPdf} />} colorScheme="red" variant="outline" size="sm"
                                onClick={handleExportPDF}>
                                Export PDF
                            </Button>
                        )}
                        <Button variant="ghost" onClick={onClose} size="sm">Fermer</Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
